import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { EuStars } from "../components/EuStars";
import {
    ArrowRight,
    BellRing,
    BookOpen,
    Cpu,
    Feather,
    Gavel,
    GitCompareArrows,
    Lock,
    Network,
    Newspaper,
    Radar,
    Scale,
    ShieldCheck,
    Sparkles,
    Banknote,
} from "lucide-react";

const MODULES = [
    { icon: Sparkles, key: "research", title: "Research Assistant",
      body: "Ask any EU law question. Source-grounded, citation-first answers with follow-up context.", to: "/app/research" },
    { icon: Radar, key: "regulatory", title: "Regulatory Intelligence",
      body: "Track 30+ instruments, deadlines, and cross-regulation interplay. Run sector-specific impact analyses.", to: "/app/regulatory" },
    { icon: Gavel, key: "case-law", title: "Case Law Explorer",
      body: "Semantic search across CJEU and ECtHR case law with side-by-side comparison.", to: "/app/case-law" },
    { icon: ShieldCheck, key: "compliance", title: "Compliance Checker",
      body: "Map your operations against GDPR, AI Act, DORA, NIS2. Structured, exportable reports.", to: "/app/compliance" },
    { icon: Scale, key: "treaty", title: "Treaty Explorer",
      body: "Read the TEU, TFEU and Charter with plain-language, doctrinal and case-law tabs.", to: "/app/treaty" },
    { icon: GitCompareArrows, key: "comparative", title: "Comparative Law",
      body: "Member-state implementations and EU-vs-national comparisons, exportable to Excel.", to: "/app/comparative" },
    { icon: Feather, key: "argument", title: "Argument Generator",
      body: "Structured legal arguments with authorities, counter-arguments and rebuttals.", to: "/app/argument" },
    { icon: BellRing, key: "watchdogs", title: "Watchdogs & Alerts",
      body: "Track regulations and topics. Get flagged when anything material moves.", to: "/app/watchdogs" },
    { icon: Cpu, key: "ai-act", title: "AI Act",
      body: "Risk classification, obligations by role, conformity assessment scaffolds.", to: "/app/ai-act" },
    { icon: Lock, key: "gdpr", title: "GDPR",
      body: "ROPA templates, DPIA scaffolds, lawful-basis assessments, transfer analysis.", to: "/app/gdpr" },
    { icon: Banknote, key: "dora", title: "DORA",
      body: "ICT risk register, incident classification, third-party register.", to: "/app/dora" },
    { icon: Network, key: "nis2", title: "NIS2",
      body: "Essential/important entity mapping, obligations, transposition tracker.", to: "/app/nis2" },
    { icon: Newspaper, key: "bulletins", title: "Bulletins",
      body: "Legal insight from the editorial desk, source-grounded briefings.", to: "/app/bulletins" },
];

export default function Dashboard() {
    const { user } = useAuth();
    const [usage, setUsage] = useState(null);

    useEffect(() => {
        api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {});
    }, []);

    return (
        <div className="container-edit py-10 lg:py-14">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow">Workspace</div>
                    <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">
                        Good to see you, {user?.name?.split(" ")[0]}.
                    </h1>
                    <p className="mt-3 max-w-xl text-[#0D1B3E]/75">
                        Every module below is live. Start with Research, escalate to a
                        Compliance report, or set a Watchdog and receive material EU
                        legal developments as they happen.
                    </p>
                </div>
                <Link to="/app/research" className="btn-primary" data-testid="dashboard-cta-research">
                    Open Research <ArrowRight size={16} />
                </Link>
            </div>

            <div className="mt-10">
                <UsageCard usage={usage} planKey={user?.plan_key} />
            </div>

            <h2 className="font-display text-2xl lg:text-3xl mt-14 tracking-tight">
                Modules
            </h2>
            <p className="mt-2 text-sm text-[#0D1B3E]/70 max-w-2xl">
                Structured legal workflows, not a chat wrapper. Every module produces
                a saved work product you can revisit, refine, and export to Word or Excel.
            </p>

            <div className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="dashboard-modules-grid">
                {MODULES.map(({ key, ...rest }) => (
                    <ModuleCard key={key} moduleKey={key} {...rest} />
                ))}
            </div>

            <div className="mt-14 flex items-center gap-3 text-[#0D1B3E]/50">
                <EuStars size={22} />
                <span className="text-xs uppercase tracking-[0.22em]">
                    EuroLaw Hub · {new Date().getFullYear()}
                </span>
            </div>
        </div>
    );
}

function UsageCard({ usage, planKey }) {
    const used = usage?.used ?? 0;
    const limit = usage?.limit ?? 0;
    const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
    return (
        <div className="card-flat p-7" data-testid="usage-card">
            <div className="label-eyebrow">This month</div>
            <div className="mt-3 font-display text-3xl tracking-tight">
                <span data-testid="usage-used">{used}</span>
                <span className="text-[#0D1B3E]/40"> / </span>
                <span data-testid="usage-limit">{limit}</span>
                <span className="text-sm uppercase tracking-[0.2em] text-[#0D1B3E]/55 ml-2">
                    credits used
                </span>
            </div>
            <div className="mt-5 h-1.5 bg-[#0D1B3E]/10">
                <div
                    className="h-full bg-[#C9A84C]"
                    style={{ width: `${pct}%` }}
                    data-testid="usage-bar"
                />
            </div>
            <div className="mt-4 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-[#0D1B3E]/60">
                <span>{planKey} plan</span>
                <Link to="/app/pricing" className="hover:text-[#C9A84C]" data-testid="usage-upgrade-link">
                    Upgrade →
                </Link>
            </div>
        </div>
    );
}

function ModuleCard({ icon: Icon, title, body, to, moduleKey }) {
    const testKey = (moduleKey || title).toLowerCase().replace(/[^a-z0-9]+/g, "-");
    return (
        <Link
            to={to}
            className="card-flat p-7 block group"
            data-testid={`dashboard-module-${testKey}`}
        >
            <div className="flex items-center gap-3">
                <Icon size={18} color="#C9A84C" />
                <span className="label-eyebrow" style={{ color: "#C9A84C" }}>
                    Live
                </span>
            </div>
            <div className="font-display text-xl mt-3 tracking-tight">{title}</div>
            <p className="mt-2 text-sm text-[#0D1B3E]/75">{body}</p>
            <div className="mt-5 text-xs uppercase tracking-[0.2em] text-[#0D1B3E]/55 group-hover:text-[#C9A84C]">
                Open →
            </div>
        </Link>
    );
}
